import { useCallback, useEffect } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';

/**
 * Hook to manage query data fetching and processing for AllQueries page
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query string
 * @param {Object} params.filters - Filter object
 * @param {Function} params.setAllQueries - Setter for all queries
 * @param {Function} params.setLoading - Setter for loading state
 * @param {Function} params.setAssignedFilterOptions - Setter for assigned filter options
 * @param {Function} params.setTotal - Setter for total count
 * @returns {Function} Fetch queries function
 */
export const useAllQueriesPageData = ({
  searchQuery,
  filters,
  setAllQueries,
  setLoading,
  setAssignedFilterOptions,
  setBackendCounts,
  setTotal,
}) => {
  const { showSnackbar } = useSnackbar();

  // Fetch ED users by region
  useEffect(() => {
    const fetchEdUsers = async () => {
      try {
        const response = await api.get('/auth/ed-users-by-region');
        if (response.data?.success && response.data?.data?.edByRegion) {
          // This will be handled by the state hook
        }
      } catch (error) {
        // console.error('Failed to load ED users:', error);
        // Don't show error to user, just log it
      }
    };

    fetchEdUsers();
  }, []);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch a larger set to enable region filtering, then paginate client-side
      const params = {
        page: 1,
        limit: 1000, // Fetch more to enable region filtering
      };

      // Add search parameter if present
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await api.get('/queries', { params });

      // ── Backend counts (single source of truth for stat cards) ──────────────
      const allQueriesCounts = response.data?.counts?.allQueries || null;
      if (setBackendCounts) setBackendCounts(allQueriesCounts);
      // ────────────────────────────────────────────────────────────────────────

      let fetched = response.data.data.queries || [];

      // Populate assignee filter options
      const assigneeMap = new Map();
      let includeUnassigned = false;
      fetched.forEach((query) => {
        const assignee = query?.assignee;
        if (assignee && (assignee._id || assignee.id)) {
          const id = assignee._id || assignee.id;
          const name =
            assignee.name ||
            assignee.fullName ||
            assignee.username ||
            'Unnamed';
          if (!assigneeMap.has(id)) {
            assigneeMap.set(id, name);
          }
        } else {
          includeUnassigned = true;
        }
      });
      const assigneeOptions = [];
      if (includeUnassigned) {
        assigneeOptions.push({ value: 'unassigned', label: 'Unassigned' });
      }
      const sortedAssignees = Array.from(assigneeMap.entries())
        .sort((a, b) =>
          (a[1] || '').localeCompare(b[1] || '', undefined, {
            sensitivity: 'base',
          })
        )
        .map(([value, label]) => ({ value, label }));
      assigneeOptions.push(...sortedAssignees);
      setAssignedFilterOptions(assigneeOptions);

      // Client-side filtering
      const trimmedSearch = (searchQuery || '').trim().toLowerCase();
      if (trimmedSearch) {
        fetched = fetched.filter((q) => {
          const id = (q.queryId || q._id || '').toString().toLowerCase();
          const header = (q.queryHeader || '').toLowerCase();
          const desc = (q.queryDescription || '').toLowerCase();
          const project = (q.projectName || '').toLowerCase();
          return (
            id.includes(trimmedSearch) ||
            header.includes(trimmedSearch) ||
            desc.includes(trimmedSearch) ||
            project.includes(trimmedSearch)
          );
        });
      }

      // Filter by assignedTo
      if (filters.assignedTo) {
        if (filters.assignedTo === 'unassigned') {
          fetched = fetched.filter(
            (query) => !query.assignee || !(query.assignee._id || query.assignee.id)
          );
        } else {
          fetched = fetched.filter((query) => {
            const assigneeId =
              query.assignee?._id ||
              query.assignee?.id ||
              (typeof query.assignee === 'string' ? query.assignee : null);
            return (
              assigneeId &&
              assigneeId.toString().toLowerCase() === filters.assignedTo.toString().toLowerCase()
            );
          });
        }
      }

      setAllQueries(fetched);
      // Prefer accurate count for the current view
      setTotal(trimmedSearch ? fetched.length : response.data.data.pagination.total);
    } catch (error) {
      // console.error('Error fetching queries:', error);
      showSnackbar('Failed to load queries', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, setAllQueries, setLoading, setAssignedFilterOptions, setBackendCounts, setTotal, showSnackbar]);

  // Fetch queries when search or filters change
  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters]);

  return {
    fetchQueries,
  };
};
